/**
 * 批量事务抽屉 - 工具函数集合
 * 提取通用的工具函数，提高代码复用性和可测试性
 */

/**
 * 从航班列表中提取机场代码
 * @param {Array} flights - 航班列表
 * @returns {Array} 机场代码数组
 */
export const extractAirportsFromFlights = (flights) => {
    if (!flights || !Array.isArray(flights)) {
        return [];
    }
    
    const airports = new Set();
    flights.forEach((flight) => {
        if (flight.departure_airport) airports.add(flight.departure_airport);
        if (flight.arrival_airport) airports.add(flight.arrival_airport);
    });
    return Array.from(airports);
};

/**
 * 生成临时ID
 * @returns {string} 临时ID字符串
 */
export const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * 查找事务
 * @param {Array} transactions - 事务列表
 * @param {string|number} transactionId - 事务ID
 * @returns {Object|undefined} 找到的事务对象
 */
export const findTransactionById = (transactions, transactionId) => {
    if (!transactions || !Array.isArray(transactions)) {
        return undefined;
    }
    return transactions.find((t) => t.id === transactionId);
};

/**
 * 查找明细
 * @param {Object} transaction - 事务对象
 * @param {string|number} detailId - 明细ID
 * @returns {Object|undefined} 找到的明细对象
 */
export const findDetailById = (transaction, detailId) => {
    if (!transaction || !transaction.details || !Array.isArray(transaction.details)) {
        return undefined;
    }
    return transaction.details.find((d) => d.id === detailId);
};

/**
 * 根据机场筛选航班
 * @param {Array} flights - 航班列表
 * @param {Array} selectedAirports - 选中的机场列表
 * @param {string} transactionType - 事务类型，用于确定筛选规则
 * @returns {Array} 筛选后的航班列表
 */
export const filterFlightsByAirports = (flights, selectedAirports, transactionType = null) => {
    if (!flights || !Array.isArray(flights)) {
        return [];
    }
    
    if (!selectedAirports || !Array.isArray(selectedAirports) || selectedAirports.length === 0) {
        return [];
    }
    
    // 对于配餐/燃油订单，只匹配始发机场
    // 对于其他事务类型，匹配始发或目的地机场
    if (transactionType === "CATERING_ORDER" || transactionType === "FUEL_REQUEST") {
        return flights.filter(flight => 
            selectedAirports.includes(flight.departure_airport)
        );
    } else {
        return flights.filter(flight => 
            selectedAirports.includes(flight.departure_airport) || 
            selectedAirports.includes(flight.arrival_airport)
        );
    }
};

/**
 * 检查航班是否匹配机场筛选条件
 * @param {Object} flight - 航班对象
 * @param {Array} selectedAirports - 选中的机场列表
 * @returns {boolean} 是否匹配
 */
export const isFlightMatchingAirports = (flight, selectedAirports) => {
    if (!flight || !selectedAirports || !Array.isArray(selectedAirports) || selectedAirports.length === 0) {
        return false;
    }
    
    return selectedAirports.includes(flight.departure_airport) || 
           selectedAirports.includes(flight.arrival_airport);
};

/**
 * 格式化航线显示
 * @param {Object} flight - 航班对象
 * @returns {string} 格式化的航线字符串
 */
export const formatRoute = (flight) => {
    if (!flight) {
        return '';
    }
    
    const departure = flight.departure_airport || '';
    const arrival = flight.arrival_airport || '';
    
    return `${departure}-${arrival}`;
};

/**
 * 深拷贝对象
 * @param {any} obj - 要拷贝的对象
 * @returns {any} 拷贝后的对象
 */
export const deepClone = (obj) => {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    
    if (obj instanceof Date) {
        return new Date(obj.getTime());
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => deepClone(item));
    }
    
    const cloned = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            cloned[key] = deepClone(obj[key]);
        }
    }
    
    return cloned;
};

/**
 * 检查对象是否为空
 * @param {any} obj - 要检查的对象
 * @returns {boolean} 是否为空
 */
export const isEmpty = (obj) => {
    if (obj === null || obj === undefined) {
        return true;
    }
    
    if (typeof obj === 'string' || Array.isArray(obj)) {
        return obj.length === 0;
    }
    
    if (typeof obj === 'object') {
        return Object.keys(obj).length === 0;
    }
    
    return false;
};

/**
 * 安全的JSON解析
 * @param {string} jsonString - JSON字符串
 * @param {any} defaultValue - 默认值
 * @returns {any} 解析结果或默认值
 */
export const safeJsonParse = (jsonString, defaultValue = null) => {
    try {
        return JSON.parse(jsonString);
    } catch (error) {
        console.warn('JSON解析失败:', error);
        return defaultValue;
    }
};

/**
 * 防抖函数
 * @param {Function} func - 要防抖的函数
 * @param {number} delay - 延迟时间（毫秒）
 * @returns {Function} 防抖后的函数
 */
export const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
};

/**
 * 节流函数
 * @param {Function} func - 要节流的函数
 * @param {number} delay - 延迟时间（毫秒）
 * @returns {Function} 节流后的函数
 */
export const throttle = (func, delay) => {
    let lastCall = 0;
    return function (...args) {
        const now = Date.now();
        if (now - lastCall >= delay) {
            lastCall = now;
            return func.apply(this, args);
        }
    };
};

/**
 * 检查事务是否有变更
 * @param {Object} transaction - 事务对象
 * @returns {boolean} 是否有变更
 */
export const hasTransactionChanges = (transaction) => {
    // 检查明细是否有变更
    const hasDetailChanges = transaction.details?.some((detail) =>
        detail.clientStatus !== 'pristine'
    ) || false;

    // 检查事务本身是否有变更
    const hasTransactionChanges = typeof transaction.id === "number" &&
        transaction.originalData && (
            transaction.status !== transaction.originalData.status ||
            transaction.remarks !== transaction.originalData.remarks
        );

    return hasDetailChanges || hasTransactionChanges;
};

/**
 * 验证事务数据
 * @param {Object} state - 状态对象
 * @returns {Object} 验证结果 { isValid, errors }
 */
export const validateTransactionData = (state) => {
    const errors = [];

    // 检查基础数据
    if (!state.selectedFlights || state.selectedFlights.length === 0) {
        errors.push('缺少航班数据');
    }

    if (!state.transactionType) {
        errors.push('缺少事务类型');
    }

    if (!state.activeAirports || state.activeAirports.length === 0) {
        errors.push('请选择至少一个机场');
    }

    // 检查事务数据
    if (!state.transactions || state.transactions.length === 0) {
        errors.push('没有可提交的事务');
    } else {
        // 检查每个事务是否有明细
        const transactionsWithoutDetails = state.transactions.filter(t =>
            (t.details || []).filter(d => d.clientStatus !== 'deleted').length === 0
        );

        if (transactionsWithoutDetails.length > 0) {
            errors.push(`${transactionsWithoutDetails.length}个事务缺少明细项`);
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * 准备提交数据载荷
 * @param {Object} state - 状态对象
 * @returns {Object} 提交载荷
 */
export const prepareSubmissionPayload = (state) => {
    const { transactions, transactionType, activeAirports, selectedSupplier } = state;

    return {
        transaction_type: transactionType,
        airports: activeAirports,
        supplier_id: selectedSupplier?.id || null,
        transactions: transactions.map(transaction => ({
            id: transaction.id,
            flight_id: transaction.flight_id,
            status: transaction.status,
            remarks: transaction.remarks,
            details: (transaction.details || [])
                .filter(detail => detail.clientStatus !== 'deleted')
                .map(detail => ({
                    id: detail.clientStatus === 'new' ? undefined : detail.id,
                    ...detail,
                    clientStatus: undefined, // 移除客户端状态
                    originalData: undefined, // 移除原始数据
                }))
        }))
    };
};

/**
 * 处理API响应
 * @param {Object} response - API响应
 * @returns {Object} 处理结果 { success, data?, errors? }
 */
export const processAPIResponse = (response) => {
    if (!response) {
        return { success: false, errors: ['无响应数据'] };
    }

    if (response.success === false) {
        return {
            success: false,
            errors: response.errors || ['操作失败']
        };
    }

    return {
        success: true,
        data: response.data || response
    };
};

/**
 * 判断工作流模式
 * @param {Array} transactions - 事务列表
 * @returns {string} 工作流模式 CREATE|EDIT|MIXED
 */
export const determineWorkflowMode = (transactions) => {
    if (!transactions || transactions.length === 0) {
        return 'CREATE';
    }

    const hasExisting = transactions.some(t => typeof t.id === 'number');
    const hasNew = transactions.some(t => typeof t.id === 'string' && t.id.startsWith('temp_'));

    if (hasExisting && hasNew) {
        return 'MIXED';
    } else if (hasExisting) {
        return 'EDIT';
    } else {
        return 'CREATE';
    }
};
